/**
 * Follow-up rewrite context (Task 170). Pure helpers that turn the chat thread
 * into the small "resolved question chain" sent to the RAG so an elliptical
 * follow-up can be rewritten into a standalone question. Extracted from App.tsx
 * (Task 201); behavior is unchanged.
 */
import type { RewriteContextTurn } from "../api/answer-client";
import type { ChatMessage } from "../state/chat-reducer";
import { FOLLOWUP_CONTEXT_TURNS } from "./answer-config";
import { ACKNOWLEDGE_ROUTE, CLARIFY_ROUTE } from "./acknowledgement";

/** Optional per-submit extras threaded to the answer client (Task 170). */
export interface AnswerExtras {
  context?: RewriteContextTurn[];
  rewrite?: boolean;
  /** Task 196: this question came from a shared /chat?q= link (as_is | edited). */
  shareOrigin?: "as_is" | "edited";
}

/**
 * Build the rewrite-only context from the thread's trailing SETTLED turns.
 *
 * CRITICAL (chained follow-ups): for each prior user turn we carry the RESOLVED
 * standalone query — `response.rewrite.standalone_query` when a rewrite was
 * applied, else the raw text — NOT the raw fragment. This advances the
 * conversational cursor: once "¿y el siguiente?" resolved to "segundo sello", a
 * SUBSEQUENT "¿y el siguiente?" resolves against "segundo sello" → "tercer
 * sello". Carrying the raw "¿y el siguiente?" twice would (wrongly) re-resolve
 * both to "segundo".
 *
 * The assistant's ANSWER prose is deliberately NEVER sent — only the resolved
 * QUESTION chain. So the answer can never leak into the rewrite as a second,
 * ungrounded source of truth (the Option-D invariant), and the context stays
 * tiny. Only settled (complete) turns contribute, mirroring the persistence rule.
 */
export function buildRewriteContext(thread: ChatMessage[]): RewriteContextTurn[] {
  const turns: RewriteContextTurn[] = [];
  for (let i = 0; i < thread.length; i += 1) {
    const m = thread[i];
    if (m === undefined || m.role !== "user") continue;
    const next = thread[i + 1];
    // A courtesy («gracias») or a fragment the server asked to complete carries
    // no topic: skipping it keeps the cursor on the last real question.
    const routeType = next !== undefined && next.role === "assistant" ? next.response?.route.type : undefined;
    if (routeType === ACKNOWLEDGE_ROUTE || routeType === CLARIFY_ROUTE) continue;
    const resolved =
      next !== undefined && next.role === "assistant" && next.status === "complete"
        ? next.response?.rewrite?.standalone_query
        : undefined;
    if (typeof resolved === "string" && resolved.trim().length > 0) {
      // A rewritten turn contributes its RESOLVED standalone — advances the cursor.
      turns.push({ role: "user", text: resolved.trim() });
    } else if (!looksLikeDependentFragment(m.text)) {
      // A turn with NO rewrite contributes its raw text ONLY if it is itself a
      // self-contained question. A bare fragment that was NOT rewritten — a
      // "literal answer" / undo turn (rewrite suppressed) or a no-context
      // refusal — must NOT poison the cursor (CTO: "raw retry should not poison
      // the conversational cursor"). Skipping it leaves the cursor on the last
      // genuinely-resolved question.
      const text = m.text.trim();
      if (text.length > 0) turns.push({ role: "user", text });
    }
  }
  return turns.slice(-FOLLOWUP_CONTEXT_TURNS);
}

/** True when a query reads as a context-dependent follow-up fragment (leads with
 * "y…"/"and…", or is a short anaphoric phrase). Used to keep an un-rewritten raw
 * fragment out of the rewrite cursor. Mirrors the RAG detector's intent. */
export function looksLikeDependentFragment(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (/^¿?\s*y\b/.test(t) || /^and\b/.test(t)) return true;
  const tokenCount = t.split(/\s+/).filter(Boolean).length;
  if (tokenCount <= 4 && /\b(eso|ese|esa|esto|anterior|siguiente|pr[oó]ximo|otro|mismo|that|next|previous|other|same)\b/.test(t)) {
    return true;
  }
  return false;
}
